/**
 * useListenerRetry — bounded re-subscription for Firestore `onSnapshot`.
 *
 * WHY. An `onSnapshot` without an error callback fails silently: the listener
 * is torn down, no data ever arrives again, and the view keeps rendering
 * whatever it last had. Nothing on screen says the stream is dead. On a
 * competition surface that reads as "the leaderboard stopped moving", which is
 * indistinguishable from "nobody is climbing".
 *
 * The recovery has to be bounded in both directions. Retrying forever turns a
 * real outage (or a genuine rules denial) into a reconnect loop that burns
 * battery and quota; not retrying at all leaves the user to discover
 * pull-to-refresh on their own.
 *
 * Usage — put `nonce` in the listener effect's dependency array so a retry
 * re-runs the effect and builds a fresh subscription:
 *
 *   const { nonce, onError, markAlive } = useListenerRetry("dashboard", compId);
 *   useEffect(() => onSnapshot(ref, snap => { markAlive(); ... }, onError("groups")),
 *             [compId, nonce, onError, markAlive]);
 *
 * `resetKey` is a string identifying what is being subscribed to; changing it
 * refills the budget, because a new competition should not inherit the previous
 * one's exhausted retries.
 *
 * The pattern originates in `useGymData`, which keeps its own inlined copy
 * because its budget is entangled with the boot loading screen.
 *
 * A denial that arrives while NOBODY is signed in is not a rules decision: the
 * session ended under the listener — a sign-out, an account deletion, or the
 * transient null Firebase emits while tabs sync. The SDK re-listens every open
 * target as signed-out the moment the session goes, and a page that holds itself
 * for a moment on a null (the /profile guard waits 2 s before it believes a
 * sign-out) is still subscribed then. That denial is expected: it is logged as a
 * warning, not an error, nothing is retried while signed out, and when a user is
 * back the listener re-subscribes — without this the friends list of a profile
 * page stayed dead after a transient null (found by the J15 browser test).
 */
import { useState, useRef, useCallback, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";

// Transient transport failures worth re-subscribing for. `permission-denied` is
// deliberately absent: it is usually a real rules decision, and the one case
// where it is not (a stale auth token) is handled by a single forced refresh.
const RETRYABLE = new Set([
  "unavailable", "deadline-exceeded", "internal",
  "cancelled", "resource-exhausted", "aborted", "unknown",
]);
const MAX_RETRIES = 4;
const MAX_BACKOFF_MS = 8000;

export function useListenerRetry(scopeLabel, resetKey = "") {
  const [nonce, setNonce] = useState(0);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const permRefreshedRef = useRef(false);
  // The session watch a denial at the session's end starts (see the header):
  // its unsubscribe, or null. Started only then — not one auth listener per
  // hook instance for the whole life of every view.
  const authWatchRef = useRef(null);
  const stopAuthWatch = useCallback(() => {
    if (authWatchRef.current) { authWatchRef.current(); authWatchRef.current = null; }
  }, []);

  const requestRetry = useCallback((delayMs) => {
    if (attemptsRef.current >= MAX_RETRIES) return false;
    if (timerRef.current) return true; // one already scheduled
    attemptsRef.current += 1;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setNonce((n) => n + 1);
    }, delayMs);
    return true;
  }, []);

  /** Call from a snapshot handler: data arrived, so the budget is refilled. */
  const markAlive = useCallback(() => { attemptsRef.current = 0; }, []);

  const onError = useCallback((label) => (err) => {
    const code = err && err.code;
    if (code === "permission-denied" && !auth.currentUser) {
      console.warn(`[${scopeLabel}] ${label} listener stopped: the session ended under it`);
      // Wait for a user to be back, then re-subscribe once.
      if (!authWatchRef.current) {
        authWatchRef.current = onAuthStateChanged(auth, (user) => {
          if (!user) return;
          stopAuthWatch();
          setNonce((n) => n + 1);
        });
      }
      return;
    }
    console.error(`[${scopeLabel}] ${label} listener error:`, err?.code, err?.message);
    if (RETRYABLE.has(code)) {
      requestRetry(Math.min(1000 * Math.pow(2, attemptsRef.current), MAX_BACKOFF_MS));
    } else if (code === "permission-denied" && !permRefreshedRef.current && auth.currentUser) {
      permRefreshedRef.current = true;
      auth.currentUser.getIdToken(true).catch(() => {}).finally(() => requestRetry(300));
    }
  }, [scopeLabel, requestRetry, stopAuthWatch]);

  /** Manual retry (a "try again" button): full budget, immediate re-subscribe. */
  const reset = useCallback(() => {
    attemptsRef.current = 0;
    permRefreshedRef.current = false;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    attemptsRef.current = 0;
    permRefreshedRef.current = false;
    stopAuthWatch();
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    return () => {
      stopAuthWatch();
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [resetKey, stopAuthWatch]);

  return { nonce, onError, markAlive, requestRetry, reset };
}
